import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { CheckCircle2, Loader2, Pen, Shield, Type } from "lucide-react";
import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { format } from "date-fns";

interface SignatureVerificationProps {
  documentId: string;
}

export function SignatureVerification({ documentId }: SignatureVerificationProps) {
  const { data: signatures, isLoading } = useQuery({
    queryKey: ["signatures", documentId],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("signatures")
        .select("*")
        .eq("document_id", documentId)
        .order("signed_at", { ascending: true });
      if (error) throw error;
      return data;
    },
  });

  if (isLoading) {
    return (
      <div className="flex items-center justify-center p-4">
        <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
      </div>
    );
  }

  if (!signatures || signatures.length === 0) return null;

  return (
    <Card>
      <CardHeader className="pb-3">
        <CardTitle className="flex items-center gap-2 text-base">
          <Shield className="h-5 w-5 text-primary" />
          E-Signatures ({signatures.length})
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-3">
        {signatures.map((sig) => {
          const signatureMethod = sig.signature_method || "drawn";
          const signatureValue = sig.signature_data_url || sig.signature_data;
          const signatureHash = sig.hash || sig.signature_hash;
          const signerLabel = sig.signer_name || sig.signer_full_name;

          return (
            <div key={sig.id} className="space-y-3 rounded-lg border border-border p-4">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  {signatureMethod === "drawn" ? (
                    <Pen className="h-4 w-4 text-muted-foreground" />
                  ) : (
                    <Type className="h-4 w-4 text-muted-foreground" />
                  )}
                  <span className="text-sm font-medium text-foreground">{signerLabel}</span>
                  <Badge variant="secondary" className="text-xs">
                    {signatureMethod === "drawn" ? "Hand-drawn" : "Typed"}
                  </Badge>
                </div>
                <Badge className="border-primary/20 bg-primary/10 text-primary">
                  <CheckCircle2 className="mr-1 h-3 w-3" />
                  Verified
                </Badge>
              </div>

              <div className="rounded-lg border border-border bg-muted/30 p-3">
                {signatureMethod === "drawn" ? (
                  <img
                    src={signatureValue}
                    alt={`Signature by ${signerLabel}`}
                    loading="lazy"
                    className="mx-auto max-h-16"
                  />
                ) : (
                  <p className="text-center font-serif text-xl italic text-foreground">
                    {signatureValue}
                  </p>
                )}
              </div>

              <div className="flex items-center justify-between text-xs text-muted-foreground">
                <span>Signed: {format(new Date(sig.signed_at), "MMM d, yyyy 'at' h:mm a")}</span>
                <span className="max-w-[200px] truncate font-mono text-[10px]" title={signatureHash}>
                  Hash: {signatureHash.slice(0, 16)}...
                </span>
              </div>
            </div>
          );
        })}
      </CardContent>
    </Card>
  );
}
